import type { PageContent as MobilePageContent } from '@code-troopers/mobile-preview/types/view/pagecontent/page-types'

import { type GalleryItem as BackofficeGalleryItem } from '@/pages/gallery/gallery.type'
import {
  type DeeplinkElement,
  parseServerDeeplinkBase,
  serializeServerDeeplinkBase,
  type ServerDeeplinkElement,
} from '@/pages/menuEntries/MenuEntry.type'
import type {
  DateTimeMs,
  EventId,
  GrabEvent,
  GroupId,
} from '@/shared/types/applicationData.type'
import type { MusicGroup } from '@/shared/types/musicGroup/musicGroup.type'
import type { ProgEvent } from '@/shared/types/progEvent.type'
import type { Scene } from '@/shared/types/scene.type'

import { type TranslatableText } from '../../../../types/TranslatableText.type'

// DeeplinkElement with the editor-only translatable fields a page element
// carries on top.
export type PageElement = DeeplinkElement & {
  a11yHint?: TranslatableText
  // dims: 1000*250 ou 490*250
  image?: TranslatableText
}

export type PageChoiceData = {
  data: string
  name: TranslatableText
  hidden?: boolean
}

export type PageChoice = PageElement & {
  choices: PageChoiceData[] | null
  multiselect: boolean
}

export type PageContentLocation = 'HOME_PAGE' | 'MENU_PAGE'

// Editor variants wrap each mobile PageContent variant with: optional `id`
// (pre-save), `title` as TranslatableText, editor-only `page` and
// `isVisible`, and `elements` slots holding the editor's PageElement.

type MobileVariant<T extends MobilePageContent['type']> = Extract<
  MobilePageContent,
  { type: T }
>

type WithEditableTitle<T> = 'title' extends keyof T
  ? Omit<T, 'title'> & { title?: TranslatableText }
  : T

type WithOptionalId<T> = Omit<T, 'id'> & { id?: string }

type EditorOverlay = {
  page: PageContentLocation
  isVisible?: boolean
  // display period in the app (device time); none: always shown
  visibleFrom?: DateTimeMs
  visibleUntil?: DateTimeMs
}

type Editor<T> = WithOptionalId<WithEditableTitle<T>> & EditorOverlay

type WithEditorElements<T, E> = Omit<T, 'elements'> & { elements: E }

export type PageCustomFullContent = WithEditorElements<
  Editor<MobileVariant<'HOME_CUSTOM_FULL'>>,
  PageElement[]
>
export type PageCustomHalfContent = WithEditorElements<
  Editor<MobileVariant<'HOME_CUSTOM_HALF'>>,
  PageElement[]
>
export type PageCustomThirdContent = WithEditorElements<
  Editor<MobileVariant<'HOME_CUSTOM_THIRD'>>,
  PageElement[]
>
export type PageListContent = WithEditorElements<
  Editor<MobileVariant<'PAGE_LIST'>>,
  PageElement[]
> & {
  // Mobile's PAGE_LIST has no heightFactor; pinning it to undefined keeps
  // `parentContent.heightFactor` narrowable on the element-bearing union.
  heightFactor?: undefined
}

export type PageGuestContent = Editor<MobileVariant<'HOME_GUEST'>>
export type PageCoverFlowGuestContent = Editor<
  MobileVariant<'HOME_COVER_FLOW_GUEST'>
>
export type PageEventContent = Editor<MobileVariant<'HOME_EVENT'>>
export type PageProgramsContent = Editor<MobileVariant<'HOME_PROGRAMS'>>
export type PageLiveContent = Editor<MobileVariant<'HOME_LIVE'>>
export type PageMainImageContent = Editor<MobileVariant<'HOME_IMAGE'>>
export type PageSubMainImageContent = Editor<MobileVariant<'HOME_SUB_IMAGE'>>
export type PageYoutubeContent = Editor<MobileVariant<'HOME_YOUTUBE'>>
export type PageSocialNetworksContent = Editor<
  MobileVariant<'HOME_SOCIAL_NETWORKS'>
>
export type PageCountdownContent = Editor<MobileVariant<'HOME_COUNTDOWN'>>
export type PageAdContent = Editor<MobileVariant<'HOME_AD'>>
export type PageNotificationsCarouselContent = Editor<
  MobileVariant<'HOME_NOTIFICATIONS_CAROUSEL'>
>

export type PageContentWithElements =
  | PageListContent
  | PageCustomFullContent
  | PageCustomThirdContent
  | PageCustomHalfContent

export type PageContent =
  | PageContentWithElements
  | PageLiveContent
  | PageProgramsContent
  | PageMainImageContent
  | PageYoutubeContent
  | PageSubMainImageContent
  | PageEventContent
  | PageGuestContent
  | PageCoverFlowGuestContent
  | PageSocialNetworksContent
  | PageCountdownContent
  | PageAdContent
  | PageNotificationsCarouselContent

// Wire shape from /api/pageContent. Two divergences from the editor union:
// HOME_COUNTDOWN.dateParam can arrive as ISO string (the editor wants
// `DateTimeMs`), and elements use the flat `ServerDeeplinkElement` shape
// with `payload: unknown` (GALLERY ships `string[]` of gallery ids).
// parse/serialize below are the only way to cross this boundary.

type StrictCountdown = Extract<PageContent, { type: 'HOME_COUNTDOWN' }>

type ServerCountdown = Omit<StrictCountdown, 'dateParam'> & {
  dateParam?: string | DateTimeMs
}

type ServerPageElement = ServerDeeplinkElement & {
  image?: TranslatableText | null
  a11yHint?: TranslatableText | null
}

type ServerPageCustomFullContent = Omit<PageCustomFullContent, 'elements'> & {
  elements: ServerPageElement[]
}
type ServerPageCustomHalfContent = Omit<PageCustomHalfContent, 'elements'> & {
  elements: ServerPageElement[]
}
type ServerPageCustomThirdContent = Omit<PageCustomThirdContent, 'elements'> & {
  elements: ServerPageElement[]
}
type ServerPageListContent = Omit<PageListContent, 'elements'> & {
  elements: ServerPageElement[]
}

export type ServerPageContent =
  | Exclude<
      PageContent,
      | { type: 'HOME_COUNTDOWN' }
      | { type: 'HOME_CUSTOM_FULL' }
      | { type: 'HOME_CUSTOM_HALF' }
      | { type: 'HOME_CUSTOM_THIRD' }
      | { type: 'PAGE_LIST' }
    >
  | ServerCountdown
  | ServerPageCustomFullContent
  | ServerPageCustomHalfContent
  | ServerPageCustomThirdContent
  | ServerPageListContent

function parseServerPageElement(
  s: ServerPageElement,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): PageElement {
  return {
    ...parseServerDeeplinkBase(s, galleryRecord),
    image: s.image ?? undefined,
    a11yHint: s.a11yHint ?? undefined,
  }
}

function serializeServerPageElement(el: PageElement): ServerPageElement {
  return {
    ...serializeServerDeeplinkBase(el),
    image: el.image ?? null,
    a11yHint: el.a11yHint ?? null,
  }
}

// Joda dates may arrive as ISO strings; the editor works in milliseconds
function toDateTimeMs(value: unknown): DateTimeMs | undefined {
  if (value == null || value === '') return undefined
  return (
    typeof value === 'string' ? new Date(value).getTime() : value
  ) as DateTimeMs
}

export function parseServerPageContent(
  c: ServerPageContent,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): PageContent {
  const period = c as { visibleFrom?: unknown; visibleUntil?: unknown }
  return {
    ...parseServerPageContentBody(c, galleryRecord),
    visibleFrom: toDateTimeMs(period.visibleFrom),
    visibleUntil: toDateTimeMs(period.visibleUntil),
  }
}

function parseServerPageContentBody(
  c: ServerPageContent,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): PageContent {
  switch (c.type) {
    case 'HOME_COUNTDOWN': {
      const param = c.dateParam
      const dateParam: DateTimeMs | undefined =
        typeof param === 'string'
          ? (new Date(param).getTime() as DateTimeMs)
          : param
      return { ...c, dateParam }
    }
    case 'HOME_CUSTOM_FULL':
    case 'HOME_CUSTOM_HALF':
    case 'HOME_CUSTOM_THIRD':
    case 'PAGE_LIST':
      return {
        ...c,
        elements: c.elements.map((el) =>
          parseServerPageElement(el, galleryRecord),
        ),
      }
    default:
      return c
  }
}

export function serializeServerPageContent(c: PageContent): ServerPageContent {
  switch (c.type) {
    case 'HOME_CUSTOM_FULL':
    case 'HOME_CUSTOM_HALF':
    case 'HOME_CUSTOM_THIRD':
    case 'PAGE_LIST':
      return {
        ...c,
        elements: c.elements.map(serializeServerPageElement),
      }
    default:
      return c
  }
}

export function sortCarouselGuestIds(
  guestIds: GroupId[],
  guests: Partial<Record<string, MusicGroup>>,
): GroupId[] {
  return [...guestIds].sort((a, b) => {
    const weightA = guests[a]?.weight ?? 0
    const weightB = guests[b]?.weight ?? 0
    return weightB - weightA
  })
}

export function sortCarouselEventIds(
  eventIds: string[],
  events: Partial<Record<string, ProgEvent | GrabEvent>>,
  scenes: Partial<Record<string, Scene>>,
  guests: Partial<Record<string, MusicGroup>>,
  programSort: 'by_hour' | 'by_importance' = 'by_hour',
): EventId[] {
  return [...eventIds].sort((a, b) => {
    const eventA = events[a]
    const eventB = events[b]
    if (!eventA || !eventB) return 0

    if (programSort === 'by_importance') {
      const sceneIdA = 'sceneId' in eventA ? eventA.sceneId : eventA.scene._id
      const sceneIdB = 'sceneId' in eventB ? eventB.sceneId : eventB.scene._id
      const sceneA = sceneIdA ? scenes[sceneIdA] : undefined
      const sceneB = sceneIdB ? scenes[sceneIdB] : undefined
      const weightA = sceneA?.weight ?? 0
      const weightB = sceneB?.weight ?? 0
      if (weightA !== weightB) {
        return weightB - weightA
      }
      const guestIdsA =
        'musicGroupsIds' in eventA
          ? eventA.musicGroupsIds
          : eventA.musicGroups?.map((g) => g._id)
      const guestIdsB =
        'musicGroupsIds' in eventB
          ? eventB.musicGroupsIds
          : eventB.musicGroups?.map((g) => g._id)
      const guestWeightA =
        guestIdsA != null && guestIdsA.length > 0
          ? Math.max(
              ...guestIdsA.map((id) =>
                id != null ? (guests[id]?.weight ?? 0) : 0,
              ),
            )
          : 0
      const guestWeightB =
        guestIdsB != null && guestIdsB.length > 0
          ? Math.max(
              ...guestIdsB.map((id) =>
                id != null ? (guests[id]?.weight ?? 0) : 0,
              ),
            )
          : 0
      if (guestWeightA !== guestWeightB) {
        return guestWeightB - guestWeightA
      }
    }
    // Default by_hour
    const dateA = coalesceDate(eventA.showStartDate, eventA.day)
    const dateB = coalesceDate(eventB.showStartDate, eventB.day)
    return dateA.localeCompare(dateB)
  }) as EventId[]
}

function coalesceDate(...values: unknown[]): string {
  const found = values.find((v) => v != null)
  if (found == null) return ''
  if (typeof found === 'object') return JSON.stringify(found)
  return String(found as string | number | boolean)
}

/** Identifies home image header or separator contents. */
export function isPageImageContent(
  content: { type: string } | undefined,
): boolean {
  return content?.type === 'HOME_IMAGE' || content?.type === 'HOME_SUB_IMAGE'
}

/** The editor list never shows the home image header and separator. */
export function withoutImageContents<T extends { type: string }>(
  all: T[],
): T[] {
  return all.filter((content) => !isPageImageContent(content))
}

/** Home image header and separator come first, as the editor list omits them. */
export function withImageContents<T extends { type: string }>(
  all: T[],
  displayed: T[],
): T[] {
  return [...all.filter(isPageImageContent), ...displayed]
}
